"use client";

import React from "react";
import { Clock, FileText, MapPin, Phone, Mail, CircleCheck, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="104499" className="flex flex-col items-center justify-center py-12 text-center space-y-4 bg-primary/5 rounded-lg border border-primary/10 p-8">
        <div className="size-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-sans font-bold tracking-tight text-foreground">Request Sent</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-lg font-mono text-xs uppercase tracking-wider">
          Send Another Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider">Full Name</FormLabel>
              <FormControl>
                <Input type="text" placeholder="John Smith" className="h-11 bg-white dark:bg-card rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="h-11 bg-white dark:bg-card rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        required: "Phone number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(832) 000-0000" className="h-11 bg-white dark:bg-card rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-wider">Service Needed</FormLabel>
              <FormControl>
                <Input type="text" placeholder="e.g., Panel Upgrade, EV Charger, Repair" className="h-11 bg-white dark:bg-card rounded-lg" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "Address and details are required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-wider">Service Address & Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Please provide your address and a brief description of the electrical issue or project..." className="min-h-[120px] bg-white dark:bg-card resize-y rounded-lg" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm font-medium">
          {submitError}
        </div>}

      <Button type="submit" variant="destructive" size="lg" disabled={isSubmitting} className="w-full md:w-auto min-w-[200px] rounded-lg font-mono text-xs uppercase tracking-widest py-6">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Sending...
          </> : 'Request Service'}
      </Button>
    </Form>;
}
export default function ReachOurTeam() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const steps = [{
    icon: <FileText className="size-6 text-primary" />,
    title: "Submit Inquiry",
    description: "Provide your contact info and details about your electrical project or emergency."
  }, {
    icon: <Clock className="size-6 text-primary" />,
    title: "Rapid Response",
    description: "Our Richmond-based team reviews your request and calls you to schedule an expert visit."
  }, {
    icon: <CircleCheck className="size-6 text-primary" />,
    title: "Expert Execution",
    description: "A licensed electrician arrives to complete the job safely, whether it's a small repair or a full rewire."
  }];
  return <section id="schedule-a-home-visit" className="py-20 md:py-32 bg-background relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[10%] -right-[5%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[100px]" />
        <div className="absolute -bottom-[10%] -left-[5%] w-[40%] h-[40%] rounded-full bg-destructive/5 blur-[100px]" />
      </div>

      <div ref={sectionRef} className={`container mx-auto px-4 md:px-6 relative z-10 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="max-w-2xl mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-sans font-bold tracking-tight text-foreground mb-4">
            Schedule a Home Visit
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed">
            From emergency repairs to precision panel upgrades, Humberg Electrical Services provides reliable, family-owned expertise across Richmond and the surrounding areas.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 xl:col-span-8 bg-white dark:bg-card border border-border shadow-lg rounded-lg p-6 md:p-10">
            <FormProvider formId="schedule_home_visit_residential" sectionName="Reach Our Team" defaultValues={{
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              required: true
            },
            message: {
              required: true
            }
          }} submitText="Request Service" successMessage="NEW CUSTOMER INQUIRY received. Thank you for choosing Humberg Electrical Services! We will contact you shortly to discuss your needs.">
              <ContactFormInner />
            </FormProvider>
          </div>

          <div className="lg:col-span-5 xl:col-span-4 space-y-8">
            <div className="bg-secondary text-secondary-foreground rounded-lg p-8 shadow-xl relative overflow-hidden border-l-4 border-destructive">
              <div className="absolute top-0 right-0 w-32 h-32 bg-primary/20 rounded-full blur-3xl -mr-10 -mt-10" />
              
              <h3 className="text-xl font-mono text-xs uppercase tracking-widest mb-8 relative z-10 opacity-70">Process</h3>
              
              <div className="space-y-8 relative z-10">
                {steps.map((step, i) => <div key={i} data-index={i} className="flex gap-5">
                    <div className="shrink-0 mt-1 bg-white rounded-lg p-2 shadow-sm text-secondary">
                      {step.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-lg mb-1 tracking-tight">{step.title}</h4>
                      <p className="text-secondary-foreground/70 text-sm leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>

            <div className="bg-muted/50 backdrop-blur-sm border border-border rounded-lg p-8 shadow-sm space-y-6">
              <h3 className="text-lg font-sans font-bold tracking-tight text-foreground">Direct Contact</h3>
              
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <Phone className="size-5 text-primary shrink-0 mt-1" />
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5">Phone</p>
                    <Link to="tel:832-405-3766" className="text-foreground font-medium hover:text-primary transition-colors">
                      832-405-3766
                    </Link>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <Mail className="size-5 text-primary shrink-0 mt-1" />
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5">Email</p>
                    <Link to="mailto:admin@humbergelectric.com" className="text-foreground font-medium hover:text-primary transition-colors">
                      admin@humbergelectric.com
                    </Link>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <MapPin className="size-5 text-primary shrink-0 mt-1" />
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5">Service Area</p>
                    <p className="text-foreground text-sm font-medium">Richmond, Sugar Land, Missouri City, and surrounding Houston areas.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4 pt-4 border-t border-border">
                  <Clock className="size-5 text-destructive shrink-0 mt-1" />
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-0.5">Business Hours</p>
                    <p className="text-foreground text-sm font-medium">Mon-Fri: 7am - 5pm<br />24/7 Emergency Service Available</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}